import { ChangeEvent, KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from "react";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditLocationAltRoundedIcon from "@mui/icons-material/EditLocationAltRounded";
import { Field, Input } from "../Field";
import { Button } from "../Button";
import { Pill } from "../Pill";
import { textDir } from "../textDir";
import s from "./AddressSearch.module.scss";

/**
 * An address as the search hands it over. `found`: a picked place (its parts + pin); `text_only`: the text exactly as
 * typed, no pin; `coordinates`: a manual address — the text as typed + a pin placed by latitude / longitude.
 */
export type AddressValue = {
  mode: "found" | "text_only" | "coordinates";
  text: string;
  street_name?: string; city?: string; state?: string; zip_code?: string; country?: string;
  place_id?: string; latitude?: number; longitude?: number;
};

export type AddressSuggestion = { id: string; primary: string; secondary: string };

export type AddressSearchLabels = {
  /** List headings: "Suggestions", "Not in the list?" */
  suggestions: string; notInList: string;
  /** "Save as text only" + its line; `text` arrives already in its own <bdi>: e.g. (t) => <>“{t}” — no map pin, routes can't include it</> */
  textOnly: string; textOnlyHint: (text: ReactNode) => ReactNode;
  /** Read out (polite) when a suggestion list arrives, e.g. "2 suggestions". */
  suggestionCount: (count: number) => string;
  /** "Manual address" (the last row and the text-only link) + "Keep this text and place the map pin by latitude / longitude" */
  manualRow: string; manualRowHint: string;
  /** The button beside the field ("Manual address (coordinates)"; phone: "Manual address"). */
  manualButton: string;
  /** Status lines: "Found on map", "Text only · no map pin" + its note, "Manual address", "Find on map instead". */
  found: string; textOnlyPill: string; textOnlyNote: string; manualPill: string; findOnMap: string;
};

export type AddressSearchProps = {
  label: string;
  value: AddressValue | null;
  onChange: (value: AddressValue | null) => void;
  /** Debounced 300 ms, from 3 characters. A rejection counts as no suggestions. */
  fetchSuggestions: (text: string) => Promise<AddressSuggestion[]>;
  /** The picked suggestion's place; `typed` is the search text (to choose the answer's language). */
  fetchDetails: (id: string, typed: string) => Promise<AddressValue>;
  labels: AddressSearchLabels;
  /** "Manual address" (last row / text-only link) hands over the typed text; the button beside the field hands over what the field shows (the contact's address, or nothing when it is empty). The caller opens CoordinatesDialog. */
  onOpenCoordinates: (text: string) => void;
  /** Shown on the manual-address button's row, after the button (a phone form puts the apartment there); on its own row when the address is text only. */
  besideManual?: ReactNode;
  hint?: string;
  error?: ReactNode;
  /** More ids for the input's aria-describedby (e.g. a message under the field). */
  describedBy?: string;
  placeholder?: string;
  /** The field label's muted note, e.g. "(optional)". */
  optional?: string;
  /** Every keystroke's text (a form can tell "typed but nothing picked" from an empty field). */
  onTextChange?: (text: string) => void;
};

type Row = { kind: "place"; s: AddressSuggestion } | { kind: "text" } | { kind: "coords" };

const PLACE_ICON = <PlaceOutlinedIcon />;
const TEXT_ICON = <NotesRoundedIcon />;
const COORDS_ICON = <MyLocationRoundedIcon />;
const DEBOUNCE_MS = 300;
const MIN_CHARS = 3;

/**
 * Board 9b / 31 B: a combobox over address suggestions whose last two rows are always "Save as text only" and
 * "Manual address", whatever is typed; the "Manual address (coordinates)" button under it; the state of the address.
 * Editing a found address drops the pick (the place no longer matches the text); a text-only or manual address
 * keeps its mode and takes the new text.
 */
export const AddressSearch = ({ label, value, onChange, fetchSuggestions, fetchDetails, labels, onOpenCoordinates, besideManual, hint, error, placeholder, optional, onTextChange, describedBy }: AddressSearchProps) => {
  const id = useId();
  const [text, setText] = useState(value?.text ?? "");
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const request = useRef(0);
  // Bumped by every keystroke and every pick: a place answer for an older pick is dropped.
  const pickRequest = useRef(0);
  const [announced, setAnnounced] = useState<number | null>(null);
  const alive = useRef(true);
  const lastEmitted = useRef<AddressValue | null>(value);
  const fetchRef = useRef(fetchSuggestions);
  fetchRef.current = fetchSuggestions;

  // Set on every mount: StrictMode runs the cleanup once and mounts again.
  useEffect(() => { alive.current = true; return () => { alive.current = false; clearTimeout(timer.current); }; }, []);

  // An outside change (form reset, coordinates saved): follow it, unless it is our own value coming back.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setText(value?.text ?? "");
  }, [value]);

  const emit = (v: AddressValue | null) => { lastEmitted.current = v; onChange(v); };

  // A new list: no row is active any more (the old index would point at a different place).
  const arrived = (list: AddressSuggestion[]) => { setSuggestions(list); setActive(-1); setAnnounced(list.length); };

  const search = (v: string) => {
    const req = ++request.current;
    fetchRef.current(v).then(
      (list) => { if (alive.current && req === request.current) arrived(list); },
      () => { if (alive.current && req === request.current) arrived([]); },
    );
  };

  const type = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    pickRequest.current++;
    setText(v);
    onTextChange?.(v);
    setActive(-1);
    setAnnounced(null);
    setOpen(v.trim() !== "");
    clearTimeout(timer.current);
    if (v.trim().length >= MIN_CHARS) timer.current = setTimeout(() => search(v), DEBOUNCE_MS);
    else { request.current++; setSuggestions([]); }
    if (value?.mode === "found") emit(null);
    else if (value) emit({ ...value, text: v });
  };

  const rows: Row[] = [...suggestions.map((sg) => ({ kind: "place" as const, s: sg })), { kind: "text" }, { kind: "coords" }];
  const optionId = (i: number) => `${id}-opt-${i}`;

  const close = () => { setOpen(false); setActive(-1); setAnnounced(null); };
  const pick = (row: Row) => {
    close();
    clearTimeout(timer.current);
    const req = ++pickRequest.current;
    if (row.kind === "text") { emit({ mode: "text_only", text: text.trim() }); return; }
    if (row.kind === "coords") { onOpenCoordinates(text); return; }
    const typed = text;
    fetchDetails(row.s.id, typed).then(
      (place) => { if (!alive.current || req !== pickRequest.current) return; setText(place.text); emit(place); },
      () => { /* The pick failed (network/quota): the text stays, the address stays unpicked and the form says so. */ },
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    const n = rows.length;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!text.trim()) return;
      e.preventDefault();
      const down = e.key === "ArrowDown";
      if (!open) { setOpen(true); setActive(down ? 0 : n - 1); return; }
      setActive((a) => (down ? (a + 1) % n : a <= 0 ? n - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (open && active >= 0 && rows[active]) { e.preventDefault(); pick(rows[active]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); e.stopPropagation(); close(); }
    }
  };

  const findOnMap = () => {
    inputRef.current?.focus();
    if (!text.trim()) return;
    setOpen(true);
    if (text.trim().length >= MIN_CHARS) search(text);
  };

  const noteId = `${id}-note`;
  const mode = value?.mode;
  // Callers put user text (place names, the typed text) in <bdi> so it cannot reorder the row.
  const option = (row: Row, i: number, icon: ReactNode, primary: ReactNode, secondary: ReactNode, strong = false) => (
    <div key={row.kind === "place" ? row.s.id : row.kind} id={optionId(i)} role="option" aria-selected={active === i}
      className={[s.option, active === i ? s.active : ""].join(" ").trim()} onClick={() => pick(row)}>
      <span aria-hidden className={s.optIcon}>{icon}</span>
      <span className={s.optText}>
        <span className={strong ? s.strong : s.primary}>{primary}</span>
        <span className={s.secondary}>{secondary}</span>
      </span>
    </div>
  );
  const fixedAt = suggestions.length;
  const bdi = (v: string) => <bdi>{v}</bdi>;
  const textOnlyLine = labels.textOnlyHint(bdi(text.trim()));

  return (
    <Field label={label} hint={hint} error={error} optional={optional}>
      {(p) => (
        <div className={s.wrap}>
          <div className={s.anchor}>
            {/* The box reads in its text's own direction (icon and padding with it): a Latin address must not flip on a Hebrew page. */}
            <div className={s.box} dir={textDir(text)}>
            <PlaceOutlinedIcon aria-hidden className={s.lead} />
            <Input ref={inputRef} id={p.id} invalid={p.invalid} className={s.input} value={text} dir={textDir(text)} placeholder={placeholder} autoComplete="off"
              role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-list`}
              aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
              aria-describedby={[p.describedBy, mode === "text_only" ? noteId : "", describedBy].filter(Boolean).join(" ") || undefined}
              onChange={type} onKeyDown={onKeyDown} onBlur={close} />
            </div>
            {open && (
              // mousedown would blur the field and close the list before the click lands.
              <div id={`${id}-list`} role="listbox" aria-label={labels.suggestions} className={s.list} onMouseDown={(e) => e.preventDefault()}>
                {suggestions.length > 0 && (
                  <div role="group" aria-labelledby={`${id}-h1`}>
                    <div id={`${id}-h1`} role="presentation" className={s.head}>{labels.suggestions}</div>
                    {suggestions.map((sg, i) => option(rows[i], i, PLACE_ICON, bdi(sg.primary), bdi(sg.secondary)))}
                  </div>
                )}
                <div role="group" aria-labelledby={`${id}-h2`} className={suggestions.length > 0 ? s.fixed : undefined}>
                  <div id={`${id}-h2`} role="presentation" className={s.head}>{labels.notInList}</div>
                  {option(rows[fixedAt], fixedAt, TEXT_ICON, labels.textOnly, textOnlyLine, true)}
                  {option(rows[fixedAt + 1], fixedAt + 1, COORDS_ICON, labels.manualRow, labels.manualRowHint, true)}
                </div>
              </div>
            )}
          </div>
          {/* Polite: the list's size when it arrives, and the address's state when it changes. */}
          <div role="status" className={s.live}>
            {open && announced !== null && <span className={VISUALLY_HIDDEN}>{labels.suggestionCount(announced)}</span>}
            {mode === "found" && <p className={s.found}><CheckCircleRoundedIcon aria-hidden className={s.statusIcon} />{labels.found}</p>}
            {mode === "coordinates" && <span className={s.pill}><Pill tone="info" icon={<EditLocationAltRoundedIcon />}>{labels.manualPill}</Pill></span>}
            {mode === "text_only" && <span className={s.pill}><Pill tone="neutral">{labels.textOnlyPill}</Pill></span>}
          </div>
          {mode === "text_only" && (
            <div className={s.textOnly}>
              <p id={noteId} className={s.note}>{labels.textOnlyNote}</p>
              <div className={s.links}>
                <Button variant="ghost" onClick={findOnMap}>{labels.findOnMap}</Button>
                <Button variant="ghost" onClick={() => onOpenCoordinates(value?.text ?? text)}>{labels.manualRow}</Button>
              </div>
              {besideManual && <div className={s.manual}>{besideManual}</div>}
            </div>
          )}
          {mode !== "text_only" && (
            <div className={s.manual}>
              <Button icon={<EditLocationAltRoundedIcon />} onClick={() => onOpenCoordinates(value?.text ?? text)}>{labels.manualButton}</Button>
              {besideManual}
            </div>
          )}
        </div>
      )}
    </Field>
  );
};
